import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersGraphqlService, type UserDashboard } from '@/services/api/users/usersGraphqlService';
import { useAuth } from '@/contexts/AuthContext';

export type SearchFilterType = 'email' | 'user_name' | 'user_type' | 'updated' | 'last_active';

interface UseUsersResult {
  users: UserDashboard[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  entriesPerPage: number;
  filterType: SearchFilterType;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  setFilterType: (type: SearchFilterType) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  refetch: () => void;
}

export const useUsers = (): UseUsersResult => {
  const { hasuraToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const [users, setUsers] = useState<UserDashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  
  const [entriesPerPage, setEntriesPerPage] = useState(() => {
    const entries = searchParams.get('entries');
    return entries ? parseInt(entries, 10) : 50;
  });
  
  const [filterType, setFilterType] = useState<SearchFilterType>(() => {
    const filter = searchParams.get('filterType') as SearchFilterType;
    return filter || 'email';
  });
  
  const [searchValue, setSearchValue] = useState(() => {
    return searchParams.get('search') || '';
  });
  
  const [activeSearch, setActiveSearch] = useState<{
    type: SearchFilterType;
    value: string;
  } | null>(() => {
    const search = searchParams.get('search');
    const filter = searchParams.get('filterType') as SearchFilterType;
    return search && filter ? { type: filter, value: search } : null;
  });

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const fetchUsers = useCallback(async () => {
    if (!hasuraToken) {
      setError('Authentication token not available');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderBy = [{ updated: 'desc' }];

      let response;

      if (activeSearch && activeSearch.value.trim()) {
        // Build search filter based on type
        const where: Record<string, any> = {};
        
        if (activeSearch.type === 'email') {
          where.email = { _ilike: `%${activeSearch.value}%` };
        } else if (activeSearch.type === 'user_name') {
          where.user_name = { _ilike: `%${activeSearch.value}%` };
        } else if (activeSearch.type === 'user_type') {
          where.user_type = { _ilike: `%${activeSearch.value}%` };
        } else if (activeSearch.type === 'updated') {
          // For date fields, search for dates greater than or equal to the entered date
          where.updated = { _gte: activeSearch.value };
        } else if (activeSearch.type === 'last_active') {
          // For date fields, search for dates greater than or equal to the entered date
          where.last_active = { _gte: activeSearch.value };
        }

        // First, get the count
        const countResponse = await usersGraphqlService.searchUsersDashboard(
          hasuraToken,
          1,
          0,
          where,
          orderBy
        );

        if (!countResponse.success || !countResponse.data) {
          throw new Error(countResponse.errors?.[0] || 'Failed to fetch user count');
        }

        const totalRecords = countResponse.data.pep_vw_user_dashboard_aggregate?.aggregate?.count || 0;
        setTotalCount(totalRecords);

        // Then fetch all records using the count as limit
        if (totalRecords > 0) {
          response = await usersGraphqlService.searchUsersDashboard(
            hasuraToken,
            totalRecords,
            0,
            where,
            orderBy
          );
        } else {
          response = countResponse;
        }
      } else {
        // First, get the count
        const countResponse = await usersGraphqlService.getUsersDashboard(
          hasuraToken,
          1,
          0,
          orderBy
        );

        if (!countResponse.success || !countResponse.data) {
          throw new Error(countResponse.errors?.[0] || 'Failed to fetch user count');
        }

        const totalRecords = countResponse.data.pep_vw_user_dashboard_aggregate?.aggregate?.count || 0;
        setTotalCount(totalRecords);

        // Then fetch all records using the count as limit
        if (totalRecords > 0) {
          response = await usersGraphqlService.getUsersDashboard(
            hasuraToken,
            totalRecords,
            0,
            orderBy
          );
        } else {
          response = countResponse;
        }
      }

      if (response.success && response.data) {
        setUsers(response.data.pep_vw_user_dashboard || []);
      } else {
        throw new Error(response.errors?.[0] || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while fetching users');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, activeSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = useCallback(() => {
    if (searchValue.trim()) {
      setActiveSearch({ type: filterType, value: searchValue });
      setCurrentPage(1);
      // Update URL params
      setSearchParams({
        page: '1',
        entries: entriesPerPage.toString(),
        filterType,
        search: searchValue
      });
    }
  }, [filterType, searchValue, entriesPerPage, setSearchParams]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch(null);
    setCurrentPage(1);
    // Clear URL params
    setSearchParams({
      page: '1',
      entries: entriesPerPage.toString()
    });
  }, [entriesPerPage, setSearchParams]);

  const handleSetCurrentPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Update URL params
      const params: Record<string, string> = {
        page: page.toString(),
        entries: entriesPerPage.toString()
      };
      if (activeSearch) {
        params.filterType = activeSearch.type;
        params.search = activeSearch.value;
      }
      setSearchParams(params);
    }
  }, [totalPages, entriesPerPage, activeSearch, setSearchParams]);

  const handleSetEntriesPerPage = useCallback((entries: number) => {
    setEntriesPerPage(entries);
    setCurrentPage(1);
    // Update URL params
    const params: Record<string, string> = {
      page: '1',
      entries: entries.toString()
    };
    if (activeSearch) {
      params.filterType = activeSearch.type;
      params.search = activeSearch.value;
    }
    setSearchParams(params);
  }, [activeSearch, setSearchParams]);

  return {
    users,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage: handleSetCurrentPage,
    setEntriesPerPage: handleSetEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch: fetchUsers,
  };
};
